import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { getIn } from 'formik'
import { useTranslation } from 'react-i18next'

import InputField from '../InputField'
import SelectField from '../SelectField'
import TextareaField from '../TextareaField'
import ErrorMessage from '../ErrorMessage'

import useFieldErrorReset from './hook'

import { getFormikFieldError } from '../../../helpers/errors'
import { capitalizeWords } from '../../../helpers/common'

import { RENDERED_FIELDS } from '../../../constants/forms'

import useStyles from './styles'

function Field({
  formik,
  symbol,
  symbolPosition,
  showSymbol,
  name,
  id,
  placeholder,
  options,
  className,
  autoComplete,
  decimalScale,
  isTextarea,
  hasEmojiPicker,
  maxLength,
  disablePlaceholderFormatting,
  enableReinitialize,
  showErrorMessage = true,
  onValueChange,
  setOnEmptyChange,
  isSearchable,
  minRows,
  hideErrorMessage,
  ...restFieldProps
}) {
  const { t } = useTranslation()

  // we use ref here to render MaxLengthMessage inside portal from child component
  const ref = useRef(null)
  const [portalElement, setPortalElement] = useState(null)

  const { values, errors, status, setStatus, touched, setFieldValue, setFieldError, setFieldTouched } = formik
  // The built-in getIn formik function allows to get and interact with nested fields like objects in array etc.
  const value = getIn(values, name) || ''
  // Same approach here, we can get errors and touched for nested fields
  const { error, serverError } = getFormikFieldError(errors, status, name)
  const isTouched = getIn(touched, name)
  const showError = !!(isTouched && error) && !hideErrorMessage

  const formattedPlaceholder = useMemo(
    () => (disablePlaceholderFormatting ? t(placeholder) : capitalizeWords(t(placeholder))),
    [t, placeholder, disablePlaceholderFormatting]
  )

  const classes = useStyles()

  const { resetError } = useFieldErrorReset({ error, errors, name, setFieldError, serverError, setStatus, status })

  useEffect(() => {
    setPortalElement(ref.current)
  }, [])

  useEffect(() => {
    // the main idea of usage is to determine if fields needed to be validated or not
    // additionally used to represent BE server errors
    setFieldValue(`${RENDERED_FIELDS}.${name}`, true)

    return () => setFieldValue(`${RENDERED_FIELDS}.${name}`, false)
  }, [name, setFieldValue])

  const renderField = () => {
    if (options) {
      return (
        <SelectField
          id={id}
          name={name}
          placeholder={placeholder}
          className={classes.selectField}
          value={value}
          hasError={showError}
          resetError={resetError}
          options={options}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          // We don't allow text search if there are only few options
          isSearchable={options.length < 8 ? false : isSearchable}
          onValueChange={onValueChange}
          {...restFieldProps}
        />
      )
    }

    if (isTextarea) {
      return (
        <TextareaField
          name={name}
          placeholder={formattedPlaceholder}
          formikValue={value}
          maxLength={maxLength}
          symbol={symbol}
          hasEmojiPicker={hasEmojiPicker}
          showError={showError}
          resetError={resetError}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          enableReinitialize={enableReinitialize}
          portalElement={portalElement}
          setOnEmptyChange={setOnEmptyChange}
          minRows={minRows}
          {...restFieldProps}
        />
      )
    }

    return (
      <InputField
        // duplicate name inside id field for field label
        id={id || name}
        name={name}
        placeholder={formattedPlaceholder}
        formikValue={value}
        symbol={symbol}
        symbolPosition={symbolPosition}
        showSymbol={showSymbol}
        showError={showError}
        resetError={resetError}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        enableReinitialize={enableReinitialize}
        autoComplete={autoComplete ? autoComplete : 'on'}
        decimalScale={decimalScale}
        onValueChange={onValueChange}
        maxLength={maxLength}
        portalElement={portalElement}
        setOnEmptyChange={setOnEmptyChange}
        {...restFieldProps}
      />
    )
  }

  return (
    // to avoid unnecessary re-renders formik sets only onBlur and field value is state manageable
    <div className={classnames('field', className, classes.field)}>
      <div className={classes.inputField}>{renderField()}</div>
      {showError && showErrorMessage && <ErrorMessage error={error} />}
    </div>
  )
}

export default Field

Field.propTypes = {
  id: PropTypes.string,
  // this required to not crash on formattedLabel - refactor if cases without label needed
  placeholder: PropTypes.string,
  symbol: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  symbolPosition: PropTypes.string,
  autoComplete: PropTypes.string,
  options: PropTypes.array,
  decimalScale: PropTypes.number,
  onEmojiChoose: PropTypes.func,
  name: PropTypes.string.isRequired,
  formik: PropTypes.object.isRequired,
  disablePlaceholderFormatting: PropTypes.bool,
  onValueChange: PropTypes.func,
  minRows: PropTypes.number
}
